import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, RefreshCcw, XCircle, Users, Upload, Trash2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BrandLogo } from "@/components/BrandLogo";
import { WebshopPanel } from "@/components/WebshopPanel";
import { ROLES } from "@/lib/constants";
import { formatDateTime } from "@/lib/format";

const STATE_MAP = {
  connected: { label: "Verbonden", tone: "healthy", icon: CheckCircle2 },
  not_connected: { label: "Niet verbonden", tone: "low", icon: AlertTriangle },
  syncing: { label: "Synchronisatie bezig", tone: "in-transit", icon: RefreshCcw },
  error: { label: "Synchronisatiefout", tone: "critical", icon: XCircle },
};

export default function Instellingen() {
  const [exact, setExact] = useState(null);
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    api.get("/exact/status").then((r) => setExact(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
  }, []);

  return (
    <div className="px-4 md:px-8 lg:px-12 py-8 md:py-10" data-testid="settings-page">
      <PageHeader eyebrow="Beheer" title="Instellingen"
        description="Alleen zichtbaar voor administrators. Beheer huisstijl, koppelingen, gebruikers en leveranciers." />

      <Tabs defaultValue="huisstijl">
        <TabsList className="mb-6 h-auto flex-wrap">
          <TabsTrigger value="huisstijl" data-testid="tab-huisstijl" className="text-base px-6 py-3">Huisstijl</TabsTrigger>
          <TabsTrigger value="webshop" data-testid="tab-webshop" className="text-base px-6 py-3">Webshop</TabsTrigger>
          <TabsTrigger value="exact" data-testid="tab-exact" className="text-base px-6 py-3">Exact Online</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users" className="text-base px-6 py-3">Gebruikers</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers" className="text-base px-6 py-3">Leveranciers</TabsTrigger>
        </TabsList>

        <TabsContent value="huisstijl" data-testid="branding-panel">
          <BrandingPanel />
        </TabsContent>

        <TabsContent value="webshop" data-testid="webshop-panel">
          <WebshopPanel />
        </TabsContent>

        <TabsContent value="exact" data-testid="exact-panel">
          {exact && <ExactStatus exact={exact} />}
        </TabsContent>

        <TabsContent value="users" data-testid="users-panel">
          <div className="surface p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Users className="w-5 h-5" /> Gebruikersrollen</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Fase 1 gebruikt geen echte inlog. Wissel van rol via de knop rechtsboven om de app te bekijken zoals Hubert, Rodilene of een magazijnmedewerker.
            </p>
            <div className="divide-y divide-border">
              {ROLES.map((r) => (
                <div key={r.id} className="py-4 flex items-start gap-4" data-testid={`user-role-${r.id}`}>
                  <div className="w-10 h-10 rounded-md bg-primary/10 text-primary grid place-items-center font-mono font-semibold">
                    {r.short[0]}
                  </div>
                  <div>
                    <div className="font-semibold">{r.name}</div>
                    <div className="text-sm text-muted-foreground">{r.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="suppliers" data-testid="suppliers-panel">
          <div className="surface overflow-hidden">
            <table className="w-full text-base">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Leverancier</th>
                  <th className="px-4 py-3">Contactpersoon</th>
                  <th className="px-4 py-3">E-mail</th>
                  <th className="px-4 py-3">Land</th>
                  <th className="px-4 py-3 text-right">Levertijd</th>
                  <th className="px-4 py-3 text-right">Open orders</th>
                  <th className="px-4 py-3 text-right">Prestaties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {suppliers.map((s) => (
                  <tr key={s.id} data-testid={`supplier-row-${s.id}`}>
                    <td className="px-4 py-3 font-medium">{s.name} {s.preferred && <span className="text-status-low">★</span>}</td>
                    <td className="px-4 py-3">{s.contact_person}</td>
                    <td className="px-4 py-3 text-muted-foreground">{s.email}</td>
                    <td className="px-4 py-3">{s.country}</td>
                    <td className="px-4 py-3 text-right font-mono">{s.lead_time_days} dagen</td>
                    <td className="px-4 py-3 text-right font-mono">{s.open_orders}</td>
                    <td className="px-4 py-3 text-right font-mono">{s.delivery_performance}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Sleutels voor huisstijl-assets in localStorage.
// LET OP: dit zijn UI-voorkeuren (data-URI's van geüploade logo's), GEEN gevoelige data.
// Geen credentials, tokens of persoonsgegevens worden hier opgeslagen.
const LOGO_KEYS = {
  full: "kipkruiden-brand-logo",
  mark: "kipkruiden-brand-mark",
  favicon: "kipkruiden-brand-favicon",
};

function BrandingPanel() {
  return (
    <div className="space-y-6">
      <div className="surface p-6" data-testid="branding-intro">
        <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
          <ImageIcon className="w-5 h-5" /> Huisstijl beheren
        </h3>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Upload het officiële Kipkruiden.nl-logo. Standaard is een tijdelijke versie ingesteld. Gebruik bij voorkeur een PNG of SVG met transparante achtergrond.
          Aanbevolen: minimaal 400px hoog voor scherpte op grote schermen.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <LogoUploader
          storageKey={LOGO_KEYS.full}
          title="Volledig logo (lichte variant)"
          hint="Wordt getoond in de linkerbovenhoek en op het inlogscherm. Optimaal op lichte achtergronden."
          bg="bg-white"
          fallback="/brand/kipkruiden-logo.png"
          testId="uploader-full"
        />
        <LogoUploader
          storageKey={LOGO_KEYS.mark}
          title="Compact merkteken (donkere variant)"
          hint="Klein logo voor favicon en kleine schermen. Gebruik hoog contrast."
          bg="bg-neutral-900"
          fallback="/brand/kipkruiden-mark.png"
          darkPreview
          testId="uploader-mark"
        />
      </div>

      <LogoUploader
        storageKey={LOGO_KEYS.favicon}
        title="Favicon"
        hint="Kleine icoon voor browser-tabs. Vierkant PNG (bijv. 64×64) aanbevolen."
        bg="bg-white"
        fallback="/favicon.ico"
        applyFavicon
        testId="uploader-favicon"
      />
    </div>
  );
}

function LogoUploader({ storageKey, title, hint, bg, fallback, darkPreview, applyFavicon, testId }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(() => localStorage.getItem(storageKey) || null);

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Bestand te groot", { description: "Maximaal 2 MB toegestaan." });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      localStorage.setItem(storageKey, dataUrl);
      setPreview(dataUrl);
      window.dispatchEvent(new Event("kipkruiden-branding-update"));
      if (applyFavicon) updateFavicon(dataUrl);
      toast.success("Logo bijgewerkt", { description: "De nieuwe versie is direct actief." });
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = () => {
    localStorage.removeItem(storageKey);
    setPreview(null);
    window.dispatchEvent(new Event("kipkruiden-branding-update"));
    if (applyFavicon) updateFavicon(null);
    toast.success("Logo verwijderd — standaard hersteld.");
  };

  const src = preview || fallback;

  return (
    <div className="surface p-6" data-testid={testId}>
      <h3 className="text-base font-semibold">{title}</h3>
      <p className="text-sm text-muted-foreground mt-1 mb-4">{hint}</p>

      <div className={`rounded-md border border-border ${bg} p-8 flex items-center justify-center min-h-[160px]`}>
        {src ? (
          <img src={src} alt="Logo voorbeeld"
            className={darkPreview ? "max-h-24 w-auto invert" : "max-h-24 w-auto"} />
        ) : (
          <div className="text-sm text-muted-foreground">Geen logo geüpload.</div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <input ref={inputRef} type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp,image/x-icon"
          onChange={handleUpload} className="hidden" data-testid={`${testId}-input`} />
        <Button onClick={() => inputRef.current?.click()} data-testid={`${testId}-select`}>
          <Upload className="w-4 h-4 mr-2" /> {preview ? "Vervangen" : "Uploaden"}
        </Button>
        {preview && (
          <Button variant="outline" onClick={handleRemove} data-testid={`${testId}-remove`}>
            <Trash2 className="w-4 h-4 mr-2" /> Verwijderen
          </Button>
        )}
        <span className="text-xs text-muted-foreground ml-auto">
          {preview ? "Aangepaste versie actief" : "Standaardlogo actief"}
        </span>
      </div>
    </div>
  );
}

function updateFavicon(dataUrl) {
  const link = document.querySelector("link[rel='icon']") || document.createElement("link");
  link.rel = "icon";
  link.href = dataUrl || "/favicon.ico";
  document.head.appendChild(link);
}

function ExactStatus({ exact }) {
  const info = STATE_MAP[exact.state] || STATE_MAP.not_connected;
  const Icon = info.icon;
  const toneBg = { healthy: "bg-status-healthy/15 border-status-healthy/40 text-status-healthy",
    low: "bg-status-low/15 border-status-low/40 text-status-low",
    "in-transit": "bg-status-in-transit/15 border-status-in-transit/40 text-status-in-transit",
    critical: "bg-status-critical/15 border-status-critical/40 text-status-critical" }[info.tone];
  return (
    <div className="space-y-6">
      <div className={`surface p-6 md:p-8 border ${toneBg}`} data-testid="exact-warning">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-md ${toneBg}`}><Icon className="w-6 h-6" /></div>
          <div className="flex-1">
            <div className="label-eyebrow">Status</div>
            <h2 className="text-xl md:text-2xl font-semibold mt-1">{info.label}</h2>
            <p className="text-muted-foreground mt-2 max-w-2xl">{exact.message}</p>
            <Button disabled className="mt-4 touch-tap opacity-60" data-testid="connect-exact">
              <RefreshCcw className="w-4 h-4 mr-2" /> Verbind met Exact Online (Fase 2)
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="surface p-6">
          <h3 className="text-lg font-semibold mb-4">Laatste synchronisatie</h3>
          <div className="space-y-2 text-sm">
            <Row label="Datum en tijd" value={exact.last_sync ? formatDateTime(exact.last_sync) : "Nog niet gesynchroniseerd"} />
            <Row label="Bijgewerkte producten" value={exact.last_sync_products} />
            <Row label="Foutmelding" value={exact.error_message || "Geen fouten"} />
          </div>
        </div>

        <div className="surface p-6">
          <h3 className="text-lg font-semibold mb-4">Synchronisatie-intervallen</h3>
          <ul className="space-y-3 text-base">
            {Object.entries(exact.sync_intervals).map(([k, v]) => (
              <li key={k} className="flex items-center justify-between border-b border-border last:border-0 pb-2">
                <span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span>
                <span className="font-mono font-medium">{v}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="surface p-6" data-testid="exact-secrets-panel">
        <h3 className="text-lg font-semibold mb-4">Server-side configuratie (Secrets)</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Deze variabelen worden uitsluitend server-side gelezen. Vul de geheime waarden in via
          <span className="font-medium"> Manage Publishing → Secrets</span> in de deploy-omgeving.
        </p>
        <div className="space-y-2 text-sm">
          <ConfigRow label="EXACT_CLIENT_ID" configured={exact.config?.client_id_configured} note="Geheim — invullen via Secrets" />
          <ConfigRow label="EXACT_CLIENT_SECRET" configured={exact.config?.client_secret_configured} note="Geheim — invullen via Secrets, nooit tonen" />
          <ConfigRow label="EXACT_REDIRECT_URI" configured={exact.config?.redirect_uri_configured} value={exact.config?.redirect_uri} />
          <ConfigRow label="EXACT_BASE_URL" configured={exact.config?.base_url_configured} value={exact.config?.base_url} />
        </div>
        {!exact.config?.fully_configured && (
          <div className="mt-4 text-xs text-status-low bg-status-low/10 border border-status-low/30 rounded-md p-3">
            De vier variabelen zijn aangemaakt in de backend. Zodra <span className="font-mono">EXACT_CLIENT_ID</span> en{" "}
            <span className="font-mono">EXACT_CLIENT_SECRET</span> via Secrets zijn ingevuld, kan Fase 2 worden geactiveerd.
          </div>
        )}
      </div>

      <div className="surface p-6">
        <h3 className="text-lg font-semibold mb-4">Gekoppelde entiteiten</h3>
        <div className="flex flex-wrap gap-2">
          {exact.entities.map((e) => (
            <span key={e} className="px-3 py-1 rounded-md border border-border bg-muted/40 font-mono text-xs">{e}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function ConfigRow({ label, configured, value, note }) {
  return (
    <div className="flex items-center justify-between border-b border-border last:border-0 pb-2 gap-3" data-testid={`exact-config-${label}`}>
      <div className="min-w-0">
        <div className="font-mono text-xs font-semibold">{label}</div>
        {value && <div className="text-xs text-muted-foreground truncate">{value}</div>}
        {!value && note && <div className="text-xs text-muted-foreground">{note}</div>}
      </div>
      <span className={`shrink-0 text-xs font-medium px-2 py-1 rounded-md border ${
        configured
          ? "border-status-healthy/40 text-status-healthy bg-status-healthy/10"
          : "border-status-low/40 text-status-low bg-status-low/10"
      }`}>
        {configured ? "Ingesteld" : "Niet ingesteld"}
      </span>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-border last:border-0 pb-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}
